import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  PurchaseOrder,
  CreatePurchaseOrderRequest,
  ApprovalRequest,
  RejectionRequest
} from '../models/PurchaseOrder';
import {
  PurchaseOrderApprovalTracking,
  PurchaseOrderAppliedApprovalRule,
  PurchaseOrderApprovalTrackingApproverRule,
  PurchaseOrderApprovalTrackingHistoryRow,
  PurchaseOrderApprovalTrackingStep
} from '../models/PurchaseOrderApprovalTracking';
import { environment } from '../../environments/environment';

export interface PurchaseOrderPagedResult {
  items: PurchaseOrder[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface PurchaseRequestSimpleViewItem {
  productName: string;
  quantityRequested: number;
  unit: string;
  justification?: string;
}

export interface PurchaseRequestSimpleViewResponse {
  id: number;
  description?: string;
  requestDate: string;
  status: string;
  approvalSituation?: string;
  requestedByStaffId?: number;
  requestedByName?: string;
  requestedByDepartmentName?: string;
  managerApproverStaffId?: number;
  managerApproverName?: string;
  costCenterId?: number;
  costCenterName?: string;
  notes?: string;
  items?: PurchaseRequestSimpleViewItem[];
}

export interface CreatePurchaseRequestItemPayload {
  productName: string;
  quantityRequested: number;
  unit: string;
  justification?: string;
}

export interface CreatePurchaseRequestPayload {
  description: string;
  priority: string;
  notes?: string;
  /** Quem solicita (enviado pelo cliente quando o JWT não traz StaffId / nameid). */
  requestedByStaffId?: number;
  items: CreatePurchaseRequestItemPayload[];
}

/**
 * Item enviado em PUT /purchase-requests/{id}.
 * `purchaseOrderItemId` identifica linha existente (omitir / null = item novo).
 * `itemId` referencia catálogo de produto/estoque quando aplicável.
 */
export interface UpdatePurchaseRequestItemPayload {
  purchaseOrderItemId?: number | null;
  productName: string;
  quantityRequested: number;
  unit: string;
  estimatedUnitPrice?: number | null;
  justification?: string | null;
  itemId?: number | null;
}

export interface UpdatePurchaseRequestPayload {
  description: string;
  priority: string;
  notes?: string;
  items: UpdatePurchaseRequestItemPayload[];
}

/** POST `/purchase-requests/{id}/manager-decision?code=` — decisão do gestor (Azure Function / APIM). */
export type PurchaseRequestManagerDecisionAction = 'Approve' | 'Reject' | 'RequestChanges';

export interface PurchaseRequestManagerDecisionBody {
  action: PurchaseRequestManagerDecisionAction;
  /** Obrigatório em Reject / RequestChanges conforme regra do backend; opcional em Approve. */
  comments: string;
}

/** Filtros de query para listagens paginadas (`null` tratado como ausente no HTTP). */
export interface PurchaseOrderListFilters {
  status?: string;
  requestedByStaffId?: number;
  supplierId?: number;
  costCenterId?: number;
  requestDateFrom?: string;
  requestDateTo?: string;
  expectedDeliveryFrom?: string;
  expectedDeliveryTo?: string;
  totalAmountFrom?: number | null;
  totalAmountTo?: number | null;
  priority?: string;
  requiresApproval?: boolean;
  search?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderService {
  private purchaseOrdersSubject = new BehaviorSubject<PurchaseOrder[]>([]);
  public purchaseOrders$ = this.purchaseOrdersSubject.asObservable();

  /** Base do microserviço de compras, sem `?code=` (a chave é enviada em cada request). */
  private get shoppingBaseUrl(): string {
    return this.stripShoppingMicroserviceQuery(
      String(environment.microservices?.shopping ?? '')
    );
  }

  private get purchaseOrdersBaseUrl(): string {
    return `${this.shoppingBaseUrl}/purchase-orders`;
  }

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /** Remove query/trailing slash de `microservices.shopping` para montar paths HTTP. */
  private stripShoppingMicroserviceQuery(raw: string): string {
    let s = raw.trim();
    const q = s.indexOf('?');
    if (q >= 0) {
      s = s.slice(0, q);
    }
    return s.replace(/\/+$/, '');
  }

  /** `code` embutido em `shopping: 'http://.../api?code=...'` (comum em Functions locais). */
  private getCodeFromShoppingMicroserviceUrl(): string {
    const raw = String(environment.microservices?.shopping ?? '').trim();
    const q = raw.indexOf('?');
    if (q < 0) {
      return '';
    }
    try {
      const qs = raw.slice(q + 1);
      return (new URLSearchParams(qs).get('code') ?? '').trim();
    } catch {
      return '';
    }
  }

  private authOpts(params?: HttpParams): { headers: HttpHeaders; params?: HttpParams } {
    const raw = this.authService.getToken();
    const token = typeof raw === 'string' ? raw.trim() : '';
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return params ? { headers, params } : { headers };
  }

  /**
   * Chave Azure (`code` na query) para shopping: mesma ordem em listagem e solicitação de compra.
   * `shoppingPurchaseRequestsCode` é alias opcional nos environments (sem obrigar todos os arquivos).
   */
  private getShoppingFunctionCode(): string {
    const env = environment as Record<string, unknown>;
    const candidates = [
      this.getCodeFromShoppingMicroserviceUrl(),
      environment.functionKey,
      environment.shoppingPurchaseOrdersListCode,
      env['shoppingPurchaseRequestsCode']
    ];
    for (const c of candidates) {
      const v = String(c ?? '').trim();
      if (v) {
        return v;
      }
    }
    return '';
  }

  /** Anexa `code=` às URLs do microserviço de compras quando a function key está configurada. */
  private appendShoppingFunctionCode(params: HttpParams): HttpParams {
    const fnCode = this.getShoppingFunctionCode();
    return fnCode ? params.set('code', fnCode) : params;
  }

  /**
   * Detecta o "envelope de erro" do microserviço de compras quando o backend devolve
   * HTTP 200 com `{ error, httpStatus, type, hint? }`. Converte em throwError
   * compatível com `errorService.handleHttpError` (lê `error.error`) e propaga o
   * `hint` no payload para a tela poder mostrar o detalhe completo.
   * Retorna o `resp` original quando não houver erro.
   */
  private unwrapShoppingResponse<T>(resp: T): T {
    if (!resp || typeof resp !== 'object') return resp;
    const r = resp as Record<string, unknown>;
    const errMsg = r['error'];
    if (typeof errMsg !== 'string' || !errMsg.trim()) return resp;

    const httpStatus =
      typeof r['httpStatus'] === 'number'
        ? (r['httpStatus'] as number)
        : typeof r['status'] === 'number'
          ? (r['status'] as number)
          : 409;
    const type = typeof r['type'] === 'string' ? (r['type'] as string) : '';
    const hint = typeof r['hint'] === 'string' ? (r['hint'] as string).trim() : '';

    const errClean = this.translateShoppingErrorMessage(type, errMsg.trim());
    // Hint do backend pode trazer termos técnicos (Bearer, currentStaffId…),
    // então só anexa se NÃO for um caso com tradução amigável definida.
    const useHint = hint && !this.hasFriendlyTranslation(type);
    const combined = useHint ? `${errClean}\n\n${hint}` : errClean;

    const fakeError = {
      status: httpStatus,
      statusText: type || 'Conflict',
      // `combined` em `error.error` garante toast + bloco <pre> com a dica.
      error: { error: combined, type, httpStatus, hint, originalMessage: errClean },
      message: combined,
      name: 'ShoppingSoftError'
    };
    throw fakeError;
  }

  /**
   * Mapeia `type` do envelope do microserviço de compras para mensagens amigáveis em PT.
   * Quando não há tradução específica, devolve a `fallback` original do backend.
   */
  private translateShoppingErrorMessage(type: string, fallback: string): string {
    const map: Record<string, string> = {
      not_designated_manager:
        'Apenas o gestor designado pode aprovar, reprovar ou solicitar ajustes nesta solicitação. Procure o solicitante para confirmar quem é o gestor responsável.',
      invalid_status_for_delete:
        'A solicitação só pode ser excluída enquanto estiver em status Solicitado (aguardando o gestor).'
    };
    const friendly = map[type];
    return friendly || fallback;
  }

  private hasFriendlyTranslation(type: string): boolean {
    return ['not_designated_manager', 'invalid_status_for_delete'].includes(type);
  }

  private appendPurchaseOrderFilters(params: HttpParams, filters?: PurchaseOrderListFilters): HttpParams {
    if (!filters) return params;
    if (filters.status) params = params.set('status', filters.status);
    if (filters.requestedByStaffId != null) params = params.set('requestedByStaffId', filters.requestedByStaffId.toString());
    if (filters.supplierId != null) params = params.set('supplierId', filters.supplierId.toString());
    if (filters.costCenterId != null) params = params.set('costCenterId', filters.costCenterId.toString());
    if (filters.requestDateFrom) params = params.set('requestDateFrom', filters.requestDateFrom);
    if (filters.requestDateTo) params = params.set('requestDateTo', filters.requestDateTo);
    if (filters.expectedDeliveryFrom) params = params.set('expectedDeliveryFrom', filters.expectedDeliveryFrom);
    if (filters.expectedDeliveryTo) params = params.set('expectedDeliveryTo', filters.expectedDeliveryTo);
    if (filters.totalAmountFrom != null && filters.totalAmountFrom !== undefined) {
      params = params.set('totalAmountFrom', filters.totalAmountFrom.toString());
    }
    if (filters.totalAmountTo != null && filters.totalAmountTo !== undefined) {
      params = params.set('totalAmountTo', filters.totalAmountTo.toString());
    }
    if (filters.priority) params = params.set('priority', filters.priority);
    if (filters.requiresApproval !== undefined && filters.requiresApproval !== null) {
      params = params.set('requiresApproval', String(filters.requiresApproval));
    }
    if (filters.search && filters.search.trim()) {
      params = params.set('search', filters.search.trim());
    }
    return params;
  }

  // ===== OPERAÇÕES BÁSICAS =====

  /**
   * Obtém pedidos de compra com paginação (Items/Page/PageSize/TotalItems/TotalPages)
   */
  getAllPurchaseOrdersPaginated(
    page: number = 1,
    pageSize: number = 50,
    filters?: PurchaseOrderListFilters
  ): Observable<PurchaseOrderPagedResult> {
    let params = new HttpParams()
      // Envia variantes de paginação para compatibilidade entre backends/case conventions
      .set('page', page.toString())
      .set('pageSize', pageSize.toString())
      .set('Page', page.toString())
      .set('PageSize', pageSize.toString());

    params = this.appendPurchaseOrderFilters(params, filters);

    return this.http.get<any>(this.purchaseOrdersBaseUrl, this.authOpts(params)).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items))
    );
  }

  /**
   * Lista paginada por centro de custo (fluxo «Solicitação de Compras»).
   * GET .../purchase-orders/cost-center/{costCenterId}?page=&pageSize=&code=
   * Envia Bearer token via authOpts.
   */
  getPurchaseOrdersByCostCenterPaginated(
    costCenterId: number,
    page: number = 1,
    pageSize: number = 20,
    filters?: PurchaseOrderListFilters
  ): Observable<PurchaseOrderPagedResult> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize))
      .set('Page', String(page))
      .set('PageSize', String(pageSize));

    params = this.appendShoppingFunctionCode(params);

    params = this.appendPurchaseOrderFilters(params, filters);

    const url = `${this.purchaseOrdersBaseUrl}/cost-center/${costCenterId}`;
    return this.http.get<any>(url, this.authOpts(params)).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items))
    );
  }

  /**
   * Compatibilidade: obtém todos (retorna apenas itens)
   */
  getAllPurchaseOrders(): Observable<PurchaseOrder[]> {
    return this.getAllPurchaseOrdersPaginated().pipe(
      map(result => result.items)
    );
  }

  /**
   * Obtém um pedido de compra por ID
   */
  getPurchaseOrderById(id: number): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`${this.purchaseOrdersBaseUrl}/${id}`, this.authOpts()).pipe(
      map(order => this.mapDates(order))
    );
  }

  /**
   * Visualização simplificada para fluxo "Solicitação de Compras".
   * Endpoint: /purchase-requests/{purchaseOrderId}/simple-view
   */
  getPurchaseRequestSimpleView(id: number): Observable<PurchaseRequestSimpleViewResponse> {
    const url = `${this.shoppingBaseUrl}/purchase-requests/${id}/simple-view`;
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && sid > 0) {
      params = params.set('currentStaffId', String(sid));
      params = params.set('requestedByStaffId', String(sid));
    }
    return this.http.get<PurchaseRequestSimpleViewResponse>(url, this.authOpts(params));
  }

  /**
   * Cria solicitação de compra (fluxo /purchase-requests).
   * POST `{baseUrl}/purchase-requests?costCenterId=&code=&currentStaffId=&requestedByStaffId=`
   * + Bearer. Anexa staffId na query e no body quando o backend exige identificação do colaborador.
   */
  createPurchaseRequest(
    costCenterId: number,
    body: CreatePurchaseRequestPayload
  ): Observable<unknown> {
    return this.authService.getStaffId().pipe(
      take(1),
      switchMap((staffId) => {
        let params = new HttpParams().set('costCenterId', String(costCenterId));
        if (staffId != null && staffId > 0) {
          params = params.set('currentStaffId', String(staffId));
          params = params.set('requestedByStaffId', String(staffId));
        }
        params = this.appendShoppingFunctionCode(params);
        const url = `${this.shoppingBaseUrl}/purchase-requests`;
        const payload: CreatePurchaseRequestPayload = {
          ...body,
          ...(staffId != null && staffId > 0 ? { requestedByStaffId: staffId } : {})
        };
        return this.http.post(url, payload, this.authOpts(params));
      })
    );
  }

  /**
   * Atualiza solicitação de compra existente.
   * PUT `{baseUrl}/purchase-requests/{id}?code={functionKey}&currentStaffId=`
   * Body: `UpdatePurchaseRequestPayload`.
   */
  updatePurchaseRequest(
    purchaseRequestId: number,
    body: UpdatePurchaseRequestPayload
  ): Observable<unknown> {
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && sid > 0) {
      params = params.set('currentStaffId', String(sid));
    }
    const url = `${this.shoppingBaseUrl}/purchase-requests/${purchaseRequestId}`;
    // [DEBUG-PR-UPDATE] remover após validar
    // eslint-disable-next-line no-console
    console.log('[DEBUG-PR-UPDATE] PUT', { url, params: params.toString(), body });
    return this.http.put(url, body, this.authOpts(params)).pipe(
      map((resp) => this.unwrapShoppingResponse(resp)),
      tap({
        // eslint-disable-next-line no-console
        next: (resp) => console.log('[DEBUG-PR-UPDATE] OK', resp),
        // eslint-disable-next-line no-console
        error: (err) => console.error('[DEBUG-PR-UPDATE] ERR', {
          status: (err as { status?: number })?.status,
          statusText: (err as { statusText?: string })?.statusText,
          url: (err as { url?: string })?.url,
          message: (err as { message?: string })?.message,
          error: (err as { error?: unknown })?.error
        })
      })
    );
  }

  /**
   * Exclui solicitação de compra.
   * DELETE `{baseUrl}/purchase-requests/{id}?code={functionKey}&currentStaffId=`
   */
  deletePurchaseRequest(purchaseRequestId: number): Observable<unknown> {
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && sid > 0) {
      params = params.set('currentStaffId', String(sid));
    }
    const url = `${this.shoppingBaseUrl}/purchase-requests/${purchaseRequestId}`;
    // [DEBUG-PR-DELETE] remover após validar
    // eslint-disable-next-line no-console
    console.log('[DEBUG-PR-DELETE] DELETE', {
      url,
      fullUrl: `${url}?${params.toString()}`,
      params: params.toString(),
      hasCode: params.has('code'),
      purchaseRequestId
    });
    return this.http.delete(url, this.authOpts(params)).pipe(
      map((resp) => this.unwrapShoppingResponse(resp)),
      tap({
        // eslint-disable-next-line no-console
        next: (resp) => console.log('[DEBUG-PR-DELETE] OK', resp),
        // eslint-disable-next-line no-console
        error: (err) => console.error('[DEBUG-PR-DELETE] ERR', {
          status: (err as { status?: number })?.status,
          statusText: (err as { statusText?: string })?.statusText,
          url: (err as { url?: string })?.url,
          message: (err as { message?: string })?.message,
          error: (err as { error?: unknown })?.error
        })
      })
    );
  }

  /**
   * Confirma a indicação de compra no pedido (fluxo direto, sem pré-aprovação).
   *
   * POST `{base}/api/purchase-orders/{id}/confirm-purchase?code=&currentStaffId=`
   * Body: `{ staffId }` — extraído do JWT do usuário logado (nunca digitado).
   *
   * Retorna `unknown` (a API pode evoluir o payload). Reutiliza o envelope
   * `unwrapShoppingResponse` para tratar respostas com HTTP 200 + erro embutido.
   */
  confirmPurchase(purchaseOrderId: number): Observable<unknown> {
    const sid = this.authService.getStaffIdFromToken();
    if (sid == null || !Number.isFinite(Number(sid)) || Number(sid) <= 0) {
      return throwError(() => ({
        status: 400,
        statusText: 'BadRequest',
        error: {
          error:
            'Não foi possível identificar o usuário logado para indicar a compra. Faça login novamente.'
        },
        message:
          'Não foi possível identificar o usuário logado para indicar a compra. Faça login novamente.',
        name: 'ShoppingMissingStaffId'
      }));
    }

    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    params = params.set('currentStaffId', String(sid));

    const url = `${this.purchaseOrdersBaseUrl}/${purchaseOrderId}/confirm-purchase`;
    const body = { staffId: sid };

    return this.http.post(url, body, this.authOpts(params)).pipe(
      map((resp) => this.unwrapShoppingResponse(resp)),
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Decisão do gestor na solicitação de compra.
   * POST `{base}/purchase-requests/{id}/manager-decision?code=&currentStaffId=`
   */
  submitPurchaseRequestManagerDecision(
    purchaseRequestId: number,
    body: PurchaseRequestManagerDecisionBody
  ): Observable<unknown> {
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && sid > 0) {
      params = params.set('currentStaffId', String(sid));
    }
    const url = `${this.shoppingBaseUrl}/purchase-requests/${purchaseRequestId}/manager-decision`;
    return this.http.post(url, body, this.authOpts(params)).pipe(
      map((resp) => this.unwrapShoppingResponse(resp))
    );
  }

  /**
   * Obtém um pedido de compra por número
   */
  getPurchaseOrderByNumber(orderNumber: string): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`${this.purchaseOrdersBaseUrl}/number/${orderNumber}`, this.authOpts()).pipe(
      map(order => this.mapDates(order))
    );
  }

  /**
   * Cria um novo pedido de compra
   */
  createPurchaseOrder(request: CreatePurchaseOrderRequest): Observable<PurchaseOrder> {
    return this.http.post<PurchaseOrder>(this.purchaseOrdersBaseUrl, request, this.authOpts()).pipe(
      map(order => this.mapDates(order)),
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Atualiza um pedido de compra
   */
  updatePurchaseOrder(id: number, request: CreatePurchaseOrderRequest): Observable<any> {
    return this.http.put(`${this.purchaseOrdersBaseUrl}/${id}`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Exclui um pedido de compra
   */
  deletePurchaseOrder(id: number): Observable<any> {
    return this.http.delete(`${this.purchaseOrdersBaseUrl}/${id}`, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  // ===== CONSULTAS AVANÇADAS =====

  /**
   * @deprecated Use getAllPurchaseOrdersPaginated com filtros ao invés deste método
   * Obtém pedidos de compra filtrados (mantido para compatibilidade)
   */
  getPurchaseOrdersFiltered(filters: any): Observable<PurchaseOrder[]> {
    let params = new HttpParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        params = params.set(key, filters[key].toString());
      }
    });

    // Usa o endpoint principal ao invés de /filtered (conforme documentação)
    return this.http.get<any>(this.purchaseOrdersBaseUrl, this.authOpts(params)).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos por status
   */
  getPurchaseOrdersByStatus(status: string): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/status/${status}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos por funcionário
   */
  getPurchaseOrdersByStaff(staffId: number): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/staff/${staffId}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos por fornecedor
   */
  getPurchaseOrdersBySupplier(supplierId: number): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/supplier/${supplierId}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos por centro de custo
   */
  getPurchaseOrdersByCostCenter(costCenterId: number): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/cost-center/${costCenterId}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  // ===== RELACIONAMENTOS =====

  /**
   * Obtém pedido com itens
   */
  getPurchaseOrderWithItems(id: number): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`${this.purchaseOrdersBaseUrl}/${id}/with-items`, this.authOpts()).pipe(
      map(order => this.mapDates(order))
    );
  }

  /**
   * Obtém pedido com aprovações
   */
  getPurchaseOrderWithApprovals(id: number): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`${this.purchaseOrdersBaseUrl}/${id}/with-approvals`, this.authOpts()).pipe(
      map(order => this.mapDates(order))
    );
  }

  /**
   * Obtém pedido completo (com todos os relacionamentos)
   */
  getPurchaseOrderComplete(id: number): Observable<PurchaseOrder> {
    return this.http.get<PurchaseOrder>(`${this.purchaseOrdersBaseUrl}/${id}/complete`, this.authOpts()).pipe(
      map(order => this.mapDates(this.normalizeOrderKeys(order)))
    );
  }

  /**
   * Rastreamento do fluxo de aprovação por faixa (regra aplicada, etapas pendentes/concluídas, histórico).
   */
  getApprovalTracking(purchaseOrderId: number): Observable<PurchaseOrderApprovalTracking> {
    return this.http
      .get<any>(`${this.purchaseOrdersBaseUrl}/${purchaseOrderId}/approval-tracking`, this.authOpts())
      .pipe(map((raw) => this.mapApprovalTracking(raw)));
  }

  // ===== APROVAÇÕES =====

  /**
   * Obtém pedidos pendentes de aprovação
   */
  getPendingApproval(): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/pending-approval`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos pendentes de aprovação por funcionário
   */
  getPendingApprovalByStaff(staffId: number): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/pending-approval/staff/${staffId}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Aprova um pedido de compra
   */
  approvePurchaseOrder(id: number, request: ApprovalRequest): Observable<any> {
    return this.http.post(`${this.purchaseOrdersBaseUrl}/${id}/approve`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Rejeita um pedido de compra
   */
  rejectPurchaseOrder(id: number, request: RejectionRequest): Observable<any> {
    return this.http.post(`${this.purchaseOrdersBaseUrl}/${id}/reject`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Cancela um pedido de compra
   */
  cancelPurchaseOrder(id: number, request: { staffId: number; cancellationReason: string }): Observable<any> {
    return this.http.post(`${this.purchaseOrdersBaseUrl}/${id}/cancel`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  // ===== OPERAÇÕES DE STATUS =====

  /**
   * Atualiza o status de um pedido
   */
  updatePurchaseOrderStatus(id: number, request: { staffId: number; newStatus: string; comments?: string }): Observable<any> {
    return this.http.post(`${this.purchaseOrdersBaseUrl}/${id}/status`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Registra o pedido no fluxo de aprovação por faixa de valor
   * (`PUT .../purchase-orders/{id}/send-to-approval?...&currentStaffId=&createdBy=`).
   *
   * O backend pode exigir esta chamada após as aprovações manuais e **antes**
   * de `confirm-purchase`, quando o total se enquadra em faixa cadastrada.
   */
  sendToApproval(id: number, request: { staffId: number; createdBy?: string }): Observable<unknown> {
    let params = new HttpParams();
    params = this.appendShoppingFunctionCode(params);
    const sid = this.authService.getStaffIdFromToken();
    if (sid != null && Number.isFinite(Number(sid)) && Number(sid) > 0) {
      params = params.set('currentStaffId', String(sid));
    }
    const createdBy = (request.createdBy ?? '').trim();
    if (createdBy) {
      params = params.set('createdBy', createdBy);
    }

    const body = { staffId: request.staffId };

    return this.http
      .put(`${this.purchaseOrdersBaseUrl}/${id}/send-to-approval`, body, this.authOpts(params))
      .pipe(
        map((resp) => this.unwrapShoppingResponse(resp)),
        tap(() => this.refreshPurchaseOrders())
      );
  }

  // ===== RELATÓRIOS E ESTATÍSTICAS =====

  /**
   * Obtém estatísticas de pedidos de compra
   */
  getPurchaseOrdersStatistics(from?: Date, to?: Date): Observable<any> {
    let params = new HttpParams();
    if (from) params = params.set('from', from.toISOString());
    if (to) params = params.set('to', to.toISOString());

    return this.http.get(`${this.purchaseOrdersBaseUrl}/statistics`, this.authOpts(params));
  }

  /**
   * Obtém dados do dashboard
   */
  getDashboardData(staffId?: number): Observable<any> {
    let params = new HttpParams();
    if (staffId) params = params.set('staffId', staffId.toString());

    return this.http.get(`${this.purchaseOrdersBaseUrl}/dashboard`, this.authOpts(params));
  }

  /**
   * Obtém pedidos em atraso
   */
  getOverdueOrders(): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/overdue`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  /**
   * Obtém pedidos que vencem em X dias
   */
  getOrdersDueInDays(days: number): Observable<PurchaseOrder[]> {
    return this.http.get<any>(`${this.purchaseOrdersBaseUrl}/due-in/${days}`, this.authOpts()).pipe(
      map(resp => this.mapPagedResponse(resp)),
      tap(result => this.purchaseOrdersSubject.next(result.items)),
      map(result => result.items)
    );
  }

  // ===== OPERAÇÕES EM LOTE =====

  /**
   * Atualiza status de múltiplos pedidos
   */
  bulkUpdateStatus(orderIds: number[], newStatus: string, staffId: number): Observable<any> {
    const request = { orderIds, newStatus, staffId };
    return this.http.post(`${this.purchaseOrdersBaseUrl}/bulk/status`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  /**
   * Aprova múltiplos pedidos
   */
  bulkApprove(orderIds: number[], staffId: number): Observable<any> {
    const request = { orderIds, staffId };
    return this.http.post(`${this.purchaseOrdersBaseUrl}/bulk/approve`, request, this.authOpts()).pipe(
      tap(() => this.refreshPurchaseOrders())
    );
  }

  // ===== UTILITÁRIOS =====

  /**
   * Gera um novo número de pedido
   */
  generateOrderNumber(): Observable<{ orderNumber: string }> {
    return this.http.get<{ orderNumber: string }>(`${this.purchaseOrdersBaseUrl}/generate-number`, this.authOpts());
  }

  /**
   * Valida se um número de pedido é único
   */
  validateOrderNumber(orderNumber: string): Observable<{ isUnique: boolean }> {
    return this.http.get<{ isUnique: boolean }>(`${this.purchaseOrdersBaseUrl}/validate-number/${orderNumber}`, this.authOpts());
  }

  // ===== MÉTODOS PRIVADOS =====

  /**
   * Mapeia resposta paginada (Items/items/data + Page/page + TotalItems/totalItems/count)
   * Suporta: { items }, { data: [] }, { data: { items: [] } }, { success, data: [] }
   */
  private mapPagedResponse(response: any): PurchaseOrderPagedResult {
    if (!response) {
      return { items: [], page: 1, pageSize: 50, totalItems: 0, totalPages: 0 };
    }

    const pickNum = (v: unknown, fallback: number): number => {
      const n = Number(v);
      return Number.isFinite(n) ? n : fallback;
    };

    const pickTotal = (root: any): number | undefined => {
      if (!root || typeof root !== 'object') return undefined;
      const v =
        root.TotalItems ??
        root.totalItems ??
        root.TotalRecords ??
        root.totalRecords ??
        root.TotalCount ??
        root.totalCount ??
        root.count ??
        root.totalElements ??
        root.TotalElements;
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : undefined;
    };

    // Extrair array de itens (vários formatos de API)
    const candidateArrays: any[][] = [
      // Preferir camelCase (formato atual da API de compras)
      Array.isArray(response.items) ? response.items : [],
      response.data && Array.isArray(response.data.items) ? response.data.items : [],
      Array.isArray(response.data) ? response.data : [],
      // Compatibilidade com outros contratos
      Array.isArray(response.Items) ? response.Items : [],
      response.Data && Array.isArray(response.Data.Items) ? response.Data.Items : [],
      Array.isArray(response.result) ? response.result : [],
      Array.isArray(response.records) ? response.records : [],
      Array.isArray(response.value) ? response.value : [],
      Array.isArray(response.content) ? response.content : [],
      Array.isArray(response) ? response : []
    ].filter(arr => Array.isArray(arr) && arr.length > 0);

    const expectedTotal =
      pickTotal(response) ??
      pickTotal(response?.data) ??
      pickTotal(response?.Data) ??
      pickTotal(response?.result);

    // Se houver mais de um candidato, prioriza o que melhor casa com total esperado.
    let rawItems: any[] = [];
    if (candidateArrays.length > 0) {
      rawItems =
        (expectedTotal != null
          ? candidateArrays.find(arr => arr.length === expectedTotal)
          : undefined) ??
        candidateArrays[0];
    }

    // Garantir que não é lista de status (resposta de Status API com entityType PurchaseOrder)
    const first = rawItems[0];
    if (first && first.entityType === 'PurchaseOrder' && first.code && first.name && !first.orderNumber && !first.OrderNumber) {
      rawItems = [];
    }

    const items = rawItems.map((order: any) => {
      const normalized = this.normalizeOrderKeys(order);
      this.applyWinnerSupplierToListing(normalized);
      return this.mapDates(normalized);
    });

    const nested = response?.data ?? response?.Data ?? response?.result;

    // Total de registros da lista: só metadados do envelope (cada pedido tem totalItems = qtd de linhas do pedido)
    const envelopeMeta =
      response && typeof response === 'object' && !Array.isArray(response)
        ? pickTotal({
            TotalItems: response.TotalItems,
            totalItems: response.totalItems,
            TotalRecords: response.TotalRecords,
            totalRecords: response.totalRecords,
            TotalCount: response.TotalCount,
            totalCount: response.totalCount
          })
        : undefined;

    let totalItems =
      envelopeMeta ??
      pickTotal(nested) ??
      (nested && typeof nested === 'object' ? pickTotal((nested as any).data) : undefined);

    if (totalItems === undefined && items.length > 0) {
      totalItems = items.length;
    }
    totalItems = totalItems ?? 0;

    const page = Math.max(1, pickNum(response?.Page ?? response?.page ?? nested?.page ?? nested?.Page ?? 1, 1));
    const pageSize = Math.max(
      1,
      pickNum(response?.PageSize ?? response?.pageSize ?? nested?.pageSize ?? nested?.PageSize ?? 50, 50)
    );

    let totalPages = pickNum(
      response?.TotalPages ?? response?.totalPages ?? nested?.totalPages ?? nested?.TotalPages,
      NaN
    );
    if (!Number.isFinite(totalPages) || totalPages < 1) {
      totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalItems / pageSize)) : 1;
    }

    return {
      items,
      page,
      pageSize,
      totalItems,
      totalPages
    };
  }

  /**
   * Normaliza item de linha do pedido (PascalCase / inconsistências) para o modelo do front.
   */
  private normalizePurchaseOrderItemKeys(item: any): any {
    if (!item || typeof item !== 'object') {
      return item;
    }
    const keyMap: Record<string, string> = {
      Id: 'id',
      PurchaseOrderId: 'purchaseOrderId',
      PurchaseOrderNumber: 'purchaseOrderNumber',
      ItemId: 'itemId',
      ItemName: 'itemName',
      ProductName: 'productName',
      ProductCode: 'productCode',
      Brand: 'brand',
      Model: 'model',
      Specifications: 'specifications',
      Unit: 'unit',
      QuantityRequested: 'quantityRequested',
      QuantityApproved: 'quantityApproved',
      QuantityReceived: 'quantityReceived',
      QuantityPending: 'quantityPending',
      EstimatedUnitPrice: 'estimatedUnitPrice',
      ActualUnitPrice: 'actualUnitPrice',
      EstimatedTotalPrice: 'estimatedTotalPrice',
      ActualTotalPrice: 'actualTotalPrice',
      DiscountPercentage: 'discountPercentage',
      DiscountAmount: 'discountAmount',
      Status: 'status',
      RejectionReason: 'rejectionReason',
      Description: 'description',
      Justification: 'justification',
      Priority: 'priority',
      Category: 'category',
      IsUrgent: 'isUrgent',
      IsRecurring: 'isRecurring',
      RemainingQuantity: 'remainingQuantity',
      ReceiptPercentage: 'receiptPercentage',
      IsFullyReceived: 'isFullyReceived',
      IsPartiallyReceived: 'isPartiallyReceived',
      IsOverdue: 'isOverdue',
      TotalPriceDifference: 'totalPriceDifference',
      DaysToDelivery: 'daysToDelivery',
      CreatedAt: 'createdAt',
      CreatedBy: 'createdBy',
      UpdatedAt: 'updatedAt',
      IsActive: 'isActive'
    };
    const normalized: any = {};
    for (const key of Object.keys(item)) {
      const newKey = keyMap[key] || key;
      normalized[newKey] = item[key];
    }
    return normalized;
  }

  /**
   * Preenche `supplierName` / `supplierId` na listagem a partir do vencedor da cotação ou campos extras da API.
   */
  private applyWinnerSupplierToListing(order: any): void {
    if (!order || typeof order !== 'object') return;

    const pickStr = (...vals: unknown[]): string => {
      for (const v of vals) {
        if (v === null || v === undefined) continue;
        const s = String(v).trim();
        if (s) return s;
      }
      return '';
    };

    const flatName = pickStr(
      order.supplierName,
      order.SupplierName,
      order.winnerSupplierName,
      order.WinnerSupplierName,
      order.winningSupplierName,
      order.WinningSupplierName,
      order.quotationWinnerSupplierName,
      order.QuotationWinnerSupplierName
    );
    if (flatName) {
      order.supplierName = flatName;
      return;
    }

    const headers = order.quotationHeaders ?? order.QuotationHeaders;
    if (!Array.isArray(headers) || headers.length === 0) {
      return;
    }

    for (const h of headers) {
      const proposals = h?.proposals ?? h?.Proposals ?? h?.quotations ?? h?.Quotations ?? [];
      if (!Array.isArray(proposals)) continue;

      const winner = proposals.find(
        (p: any) =>
          p?.isWinner === true ||
          p?.IsWinner === true ||
          String(p?.status || p?.Status || '')
            .toLowerCase()
            .includes('winner')
      );
      if (!winner) continue;

      const name = pickStr(winner.supplierName, winner.SupplierName);
      const sid = winner.supplierId ?? winner.SupplierId;
      if (name) {
        order.supplierName = name;
      }
      if (sid != null && Number.isFinite(Number(sid))) {
        order.supplierId = Number(sid);
      }
      return;
    }
  }

  /** Normaliza chaves do pedido (PascalCase) para o formato esperado pelo front. */
  private normalizeOrderKeys(order: any): any {
    if (!order || typeof order !== 'object') return order;
    const keyMap: { [key: string]: string } = {
      Id: 'id', OrderNumber: 'orderNumber', RequestDate: 'requestDate', ApprovalDate: 'approvalDate',
      ExpectedDeliveryDate: 'expectedDeliveryDate', ActualDeliveryDate: 'actualDeliveryDate',
      Status: 'status', Priority: 'priority', SubTotal: 'subTotal', DiscountAmount: 'discountAmount',
      ShippingCost: 'shippingCost', TaxAmount: 'taxAmount', TotalAmount: 'totalAmount',
      RequestedByStaffId: 'requestedByStaffId', RequestedByStaffName: 'requestedByStaffName',
      ApprovedByStaffId: 'approvedByStaffId', ApprovedByStaffName: 'approvedByStaffName',
      SupplierId: 'supplierId', SupplierName: 'supplierName', CostCenterId: 'costCenterId',
      CostCenterName: 'costCenterName', DeliveryWarehouseId: 'deliveryWarehouseId',
      DeliveryWarehouseName: 'deliveryWarehouseName', Description: 'description',
      RequiresApproval: 'requiresApproval', IsActive: 'isActive', Items: 'items', Approvals: 'approvals',
      Notes: 'notes', RequiresManagerApproval: 'requiresManagerApproval',
      ManagerApproverStaffId: 'managerApproverStaffId',
      ManagerApproverName: 'managerApproverName',
      RequiredAdditionalApprovalsCount: 'requiredAdditionalApprovalsCount',
      AdditionalApprovalsCompleted: 'additionalApprovalsCompleted',
      CurrentRequiredApprovalOrder: 'currentRequiredApprovalOrder',
      TotalRequiredApprovalSteps: 'totalRequiredApprovalSteps',
      IsManagerApprovalCompleted: 'isManagerApprovalCompleted',
      IsAwaitingCurrentApprovalOrder: 'isAwaitingCurrentApprovalOrder',
      RequiredAdditionalApprovers: 'requiredAdditionalApprovers',
      AvailableApprovalMatrixRules: 'availableApprovalMatrixRules',
      QuotationHeaders: 'quotationHeaders',
      CreatedAt: 'createdAt', CreatedBy: 'createdBy', UpdatedAt: 'updatedAt', UpdatedBy: 'updatedBy',
      CanBeApproved: 'canBeApproved', CanBeRejected: 'canBeRejected', CanBeCancelled: 'canBeCancelled',
      TotalItems: 'totalItems', ApprovedItems: 'approvedItems', ReceivedItems: 'receivedItems',
      ReceiptPercentage: 'receiptPercentage', DaysInCurrentStatus: 'daysInCurrentStatus',
      DaysToDelivery: 'daysToDelivery', IsOverdue: 'isOverdue', IsPartiallyReceived: 'isPartiallyReceived'
    };
    const normalized: any = {};
    for (const key of Object.keys(order)) {
      const newKey = keyMap[key] || key;
      normalized[newKey] = order[key];
    }
    return normalized;
  }

  private mapApprovalTracking(raw: any): PurchaseOrderApprovalTracking {
    if (!raw || typeof raw !== 'object') {
      return { purchaseOrderId: 0 };
    }
    const pickN = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const applied = raw.appliedRule ?? raw.AppliedRule ?? null;
    return {
      purchaseOrderId: pickN(raw.purchaseOrderId ?? raw.PurchaseOrderId) ?? 0,
      orderNumber: raw.orderNumber ?? raw.OrderNumber,
      totalAmount: pickN(raw.totalAmount ?? raw.TotalAmount),
      status: raw.status ?? raw.Status,
      appliedRule: applied ? this.mapAppliedApprovalRule(applied) : null,
      currentStep: this.mapApprovalTrackingStep(raw.currentStep ?? raw.CurrentStep),
      pendingSteps: this.mapApprovalTrackingSteps(raw.pendingSteps ?? raw.PendingSteps),
      completedSteps: this.mapApprovalTrackingSteps(raw.completedSteps ?? raw.CompletedSteps),
      statusHistory: this.mapApprovalTrackingHistoryRows(raw.statusHistory ?? raw.StatusHistory)
    };
  }

  private mapAppliedApprovalRule(raw: any): PurchaseOrderAppliedApprovalRule {
    const approversRaw = raw.additionalApprovers ?? raw.AdditionalApprovers;
    const additionalApprovers = Array.isArray(approversRaw)
      ? approversRaw.map((a: any) => this.mapRuleAdditionalApprover(a))
      : undefined;
    const pickN = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const pickId = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    return {
      id: pickId(raw.id ?? raw.Id),
      ruleName: raw.ruleName ?? raw.RuleName,
      minAmount: pickN(raw.minAmount ?? raw.MinAmount),
      maxAmount: pickN(raw.maxAmount ?? raw.MaxAmount),
      requiresManagerApproval: Boolean(raw.requiresManagerApproval ?? raw.RequiresManagerApproval),
      requiredAdditionalApprovalsCount: pickN(raw.requiredAdditionalApprovalsCount ?? raw.RequiredAdditionalApprovalsCount),
      skipApprovalWhenInRange: Boolean(raw.skipApprovalWhenInRange ?? raw.SkipApprovalWhenInRange),
      requiresSequentialApproval: Boolean(raw.requiresSequentialApproval ?? raw.RequiresSequentialApproval),
      isActive: Boolean(raw.isActive ?? raw.IsActive),
      description: raw.description ?? raw.Description,
      additionalApprovers,
      createdAt: raw.createdAt ?? raw.CreatedAt
    };
  }

  private mapRuleAdditionalApprover(raw: any): PurchaseOrderApprovalTrackingApproverRule {
    const pickN = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    return {
      approvalLevel: raw.approvalLevel ?? raw.ApprovalLevel,
      staffId: pickN(raw.staffId ?? raw.StaffId),
      staffName: raw.staffName ?? raw.StaffName,
      responsibleStaffId: pickN(raw.responsibleStaffId ?? raw.ResponsibleStaffId),
      responsibleStaffName: raw.responsibleStaffName ?? raw.ResponsibleStaffName,
      approvalOrder: pickN(raw.approvalOrder ?? raw.ApprovalOrder),
      isRequired: Boolean(raw.isRequired ?? raw.IsRequired),
      isActive: Boolean(raw.isActive ?? raw.IsActive)
    };
  }

  private mapApprovalTrackingSteps(raw: any): PurchaseOrderApprovalTrackingStep[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const rows = raw.map((x) => this.mapApprovalTrackingStep(x)).filter(Boolean) as PurchaseOrderApprovalTrackingStep[];
    return rows.length ? rows : undefined;
  }

  private mapApprovalTrackingStep(raw: any): PurchaseOrderApprovalTrackingStep | undefined {
    if (!raw || typeof raw !== 'object') return undefined;
    const pickN = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const pickId = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    return {
      id: pickId(raw.id ?? raw.Id),
      purchaseOrderId: pickN(raw.purchaseOrderId ?? raw.PurchaseOrderId),
      purchaseOrderNumber: raw.purchaseOrderNumber ?? raw.PurchaseOrderNumber,
      staffId: pickN(raw.staffId ?? raw.StaffId),
      staffName: raw.staffName ?? raw.StaffName,
      approvalDate: raw.approvalDate ?? raw.ApprovalDate,
      action: raw.action ?? raw.Action,
      approvalLevel: raw.approvalLevel ?? raw.ApprovalLevel,
      comments: raw.comments ?? raw.Comments,
      approvalOrder: pickN(raw.approvalOrder ?? raw.ApprovalOrder),
      isManagerApproval: Boolean(raw.isManagerApproval ?? raw.IsManagerApproval),
      wasEligibleInApprovalOrder: Boolean(raw.wasEligibleInApprovalOrder ?? raw.WasEligibleInApprovalOrder),
      isFinalApproval: Boolean(raw.isFinalApproval ?? raw.IsFinalApproval),
      isAutomaticApproval: Boolean(raw.isAutomaticApproval ?? raw.IsAutomaticApproval),
      isApproved: Boolean(raw.isApproved ?? raw.IsApproved),
      isRejected: Boolean(raw.isRejected ?? raw.IsRejected),
      hasRequestedChanges: Boolean(raw.hasRequestedChanges ?? raw.HasRequestedChanges),
      daysToApprove: pickN(raw.daysToApprove ?? raw.DaysToApprove),
      actionDisplayName: raw.actionDisplayName ?? raw.ActionDisplayName,
      approvalLevelDisplayName: raw.approvalLevelDisplayName ?? raw.ApprovalLevelDisplayName,
      statusColor: raw.statusColor ?? raw.StatusColor,
      statusIcon: raw.statusIcon ?? raw.StatusIcon,
      // Não usar Boolean(undefined): ausente na API deve permanecer indefinido (não “false”).
      canEdit: this.pickOptionalBool(raw.canEdit ?? raw.CanEdit),
      canCancel: this.pickOptionalBool(raw.canCancel ?? raw.CanCancel),
      createdAt: raw.createdAt ?? raw.CreatedAt,
      createdBy: raw.createdBy ?? raw.CreatedBy
    };
  }

  private mapApprovalTrackingHistoryRows(raw: any): PurchaseOrderApprovalTrackingHistoryRow[] | undefined {
    if (!Array.isArray(raw)) return undefined;
    const pickN = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    };
    const pickId = (v: unknown): number | undefined => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : undefined;
    };
    const rows: PurchaseOrderApprovalTrackingHistoryRow[] = raw.map((h: any) => ({
      id: pickId(h?.id ?? h?.Id),
      purchaseOrderId: pickN(h?.purchaseOrderId ?? h?.PurchaseOrderId),
      staffId: pickN(h?.staffId ?? h?.StaffId),
      statusDate: h?.statusDate ?? h?.StatusDate,
      previousStatus: h?.previousStatus ?? h?.PreviousStatus,
      newStatus: h?.newStatus ?? h?.NewStatus,
      action: h?.action ?? h?.Action,
      comments: h?.comments ?? h?.Comments,
      reason: h?.reason ?? h?.Reason,
      isSystemGenerated: Boolean(h?.isSystemGenerated ?? h?.IsSystemGenerated),
      isAutomaticTransition: Boolean(h?.isAutomaticTransition ?? h?.IsAutomaticTransition),
      isStatusUpgrade: Boolean(h?.isStatusUpgrade ?? h?.IsStatusUpgrade),
      isStatusDowngrade: Boolean(h?.isStatusDowngrade ?? h?.IsStatusDowngrade),
      durationInPreviousStatus: pickN(h?.durationInPreviousStatus ?? h?.DurationInPreviousStatus),
      createdAt: h?.createdAt ?? h?.CreatedAt,
      updatedAt: h?.updatedAt ?? h?.UpdatedAt,
      createdBy: h?.createdBy ?? h?.CreatedBy,
      updatedBy: h?.updatedBy ?? h?.UpdatedBy,
      isDeleted: Boolean(h?.isDeleted ?? h?.IsDeleted)
    }));
    return rows.length ? rows : undefined;
  }

  /** Preserva ausência: `Boolean(undefined)` viraria `false` e quebraria `canEdit` opcional na API. */
  private pickOptionalBool(value: unknown): boolean | undefined {
    if (value === undefined || value === null) return undefined;
    return Boolean(value);
  }

  /**
   * Converte strings de data em objetos Date
   */
  private mapDates(order: any): PurchaseOrder {
    if (order.requestDate) order.requestDate = new Date(order.requestDate);
    if (order.approvalDate) order.approvalDate = new Date(order.approvalDate);
    if (order.expectedDeliveryDate) order.expectedDeliveryDate = new Date(order.expectedDeliveryDate);
    if (order.actualDeliveryDate) order.actualDeliveryDate = new Date(order.actualDeliveryDate);
    if (order.createdAt) order.createdAt = new Date(order.createdAt);
    if (order.updatedAt) order.updatedAt = new Date(order.updatedAt);

    // Mapear datas dos itens + chaves normalizadas
    if (order.items && Array.isArray(order.items)) {
      order.items = order.items.map((raw: any) => {
        const item = this.normalizePurchaseOrderItemKeys({ ...raw });
        if (item.expectedDeliveryDate) item.expectedDeliveryDate = new Date(item.expectedDeliveryDate);
        if (item.actualDeliveryDate) item.actualDeliveryDate = new Date(item.actualDeliveryDate);
        if (item.receiptDate) item.receiptDate = new Date(item.receiptDate);
        if (item.expirationDate) item.expirationDate = new Date(item.expirationDate);
        if (item.qualityCheckDate) item.qualityCheckDate = new Date(item.qualityCheckDate);
        if (item.createdAt) item.createdAt = new Date(item.createdAt);
        if (item.updatedAt) item.updatedAt = new Date(item.updatedAt);
        return item;
      });
    }

    // Mapear datas das aprovações
    if (order.approvals && Array.isArray(order.approvals)) {
      order.approvals = order.approvals.map((approval: any) => {
        if (approval.approvalDate) approval.approvalDate = new Date(approval.approvalDate);
        if (approval.delegationExpiry) approval.delegationExpiry = new Date(approval.delegationExpiry);
        if (approval.createdAt) approval.createdAt = new Date(approval.createdAt);
        if (approval.updatedAt) approval.updatedAt = new Date(approval.updatedAt);
        return approval;
      });
    }

    return order as PurchaseOrder;
  }

  /**
   * Atualiza a lista de pedidos
   */
  private refreshPurchaseOrders(): void {
    this.getAllPurchaseOrders().subscribe();
  }

  // ===== MÉTODOS DE FORMATAÇÃO =====

  /**
   * Formata o status para exibição
   */
  getStatusDisplayName(status: string): string {
    const statusMap: { [key: string]: string } = {
      'Solicitado': 'Solicitado',
      'Em Aprovação': 'Em Aprovação',
      'Aprovado': 'Aprovado',
      'Rejeitado': 'Rejeitado',
      'Em Compra': 'Em Compra',
      'Entregue Parcialmente': 'Entregue Parcialmente',
      'Entregue Totalmente': 'Entregue Totalmente',
      'Cancelado': 'Cancelado'
    };
    return statusMap[status] || status;
  }

  /**
   * Obtém a cor do status
   */
  getStatusColor(status: string): string {
    const colorMap: { [key: string]: string } = {
      'Solicitado': 'info',
      'Em Aprovação': 'warning',
      'Aprovado': 'success',
      'Rejeitado': 'danger',
      'Em Compra': 'primary',
      'Entregue Parcialmente': 'warning',
      'Entregue Totalmente': 'success',
      'Cancelado': 'secondary'
    };
    return colorMap[status] || 'secondary';
  }

  /**
   * Obtém o ícone do status
   */
  getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      'Solicitado': 'file-text',
      'Em Aprovação': 'clock',
      'Aprovado': 'check-circle',
      'Rejeitado': 'x-circle',
      'Em Compra': 'shopping-cart',
      'Entregue Parcialmente': 'package',
      'Entregue Totalmente': 'check-square',
      'Cancelado': 'slash'
    };
    return iconMap[status] || 'circle';
  }

  /**
   * Formata prioridade para exibição
   */
  getPriorityDisplayName(priority: string): string {
    const priorityMap: { [key: string]: string } = {
      'Baixa': 'Baixa',
      'Normal': 'Normal',
      'Alta': 'Alta',
      'Urgente': 'Urgente'
    };
    return priorityMap[priority] || priority;
  }

  /**
   * Obtém a cor da prioridade
   */
  getPriorityColor(priority: string): string {
    const colorMap: { [key: string]: string } = {
      'Baixa': 'secondary',
      'Normal': 'info',
      'Alta': 'warning',
      'Urgente': 'danger'
    };
    return colorMap[priority] || 'secondary';
  }
}
